/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Link, Typography2, breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import TrackedButton from 'bundles/page/components/TrackedButton';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { noheadshotUrl } from 'bundles/partners-graphql/constants';
import HeroBadges from 'bundles/unified-description-page-common/components/modules/hero/HeroBadges';
import InstructorsModal from 'bundles/unified-description-page-common/components/shared/modals/InstructorsModal';
import { useModal } from 'bundles/unified-description-page-common/components/shared/modals/Modal';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import type {
  InstructorFragment,
  PartnerFragment,
} from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';
import { getInstructorPageUrl } from 'bundles/unified-description-page-common/utils';

import _t from 'i18n!nls/unified-description-page-common';

const styles = {
  container: css`
    display: flex;
    align-items: center;
  `,
  instructorPortraitsContainer: css`
    display: flex;
    align-items: center;
  `,
  instructorPortrait: css`
    border-radius: 50%;
    background-color: var(--cds-color-neutral-background-primary);
    padding: 2px;
    height: 36px;
    width: 36px;
    border: 1px solid var(--cds-color-grey-50);
    overflow: hidden;

    & + & {
      margin-left: calc(var(--cds-spacing-150) * -1);
    }
  `,
  instructorPortraitImage: css`
    height: 100%;
    border-radius: 50%;
  `,
  instructorLinksContainer: (displayTopInstructorBadgeInline?: boolean) => css`
    margin-left: var(--cds-spacing-100);

    ${
      displayTopInstructorBadgeInline === true &&
      `
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--cds-spacing-150);

      ${breakpoints.down('xs')} {
        align-items: start;
        gap: 0;
      }
    `
    }
  `,
  instructorLink: css`
    margin-left: 3px;
    text-decoration-color: var(--cds-color-neutral-primary);
  `,
  moreInstructorsButton: css`
    padding: 0;
    margin-left: 2px;

    /* set vertical-align to unset to vertically center the button within the "Subtitles" line */
    vertical-align: unset;

    :hover {
      background: transparent;
    }
  `,
};

// We want the 'top instructors' to be first, since we only display one full name
// and add a 'Top Instructor' tag if the 'isTopInstructor' prop is true for the first instructor.
const sortByTopInstructor = (instructors: Array<InstructorFragment>) => {
  return instructors.slice().sort((a, b) => {
    if (a.isTopInstructor && !b.isTopInstructor) {
      return -1;
    } else if (b.isTopInstructor && !a.isTopInstructor) {
      return 1;
    } else {
      return 0;
    }
  });
};

type Props = {
  instructors?: Array<InstructorFragment> | null;
  partners?: Array<PartnerFragment> | null;
  instructorRatingCount?: number | null;
  averageInstructorRating?: number | null;
  disableCTA?: boolean;
  displayTopInstructorBadgeInline?: boolean;
  slug?: string;
};

const HeroInstructors = ({
  instructors,
  averageInstructorRating,
  instructorRatingCount,
  partners,
  displayTopInstructorBadgeInline,
  disableCTA,
  slug,
}: Props) => {
  const { isModalOpen, setModalOpen } = useModal();
  const track = useTracker();
  const eventingProductData = useEventingProductData();

  if (!instructors || instructors.length === 0) {
    return null;
  }

  const sortedInstructors = sortByTopInstructor(instructors);
  const firstInstructor = sortedInstructors[0];

  const styledInstructorName = (
    <Typography2 component="span" variant="bodyPrimary">
      {/*
       // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
      {firstInstructor.fullName}
    </Typography2>
  );

  const styledInstructorsLabel = (
    <Typography2 component="span" variant="subtitleMedium">
      {_t('+#{otherInstructors} more', { otherInstructors: sortedInstructors.length - 1 })}
    </Typography2>
  );

  const onClickFirstInstructor = () => {
    track('click_button', {
      button: { name: 'description_page_instructor' },
      pageSection: {
        sectionName: 'hero_banner',
      },
      ...eventingProductData,
    });
  };

  const onClickViewMoreInstructors = () => {
    track('click_button', {
      button: { name: 'description_page_instructor_more' },
      pageSection: {
        sectionName: 'hero_banner',
      },
      ...eventingProductData,
    });
    setModalOpen();
  };

  return (
    <div css={styles.container}>
      <div css={styles.instructorPortraitsContainer}>
        {sortedInstructors.slice(0, 3).map((instructor) => {
          return (
            <div css={styles.instructorPortrait} key={instructor.id}>
              <Imgix
                src={instructor.photo || noheadshotUrl}
                alt={instructor.fullName}
                maxHeight={75}
                maxWidth={75}
                css={styles.instructorPortraitImage}
                imgParams={{ fit: 'crop' }}
              />
            </div>
          );
        })}
      </div>
      <div css={styles.instructorLinksContainer(displayTopInstructorBadgeInline)}>
        <div css={styles.container}>
          <Typography2 component="p" variant="bodyPrimary">
            <FormattedMessage
              message={_t(
                '{numberOfInstructors, plural, =1 {Instructor} other {Instructors}}: {instructorLink} {moreInstructors}'
              )}
              numberOfInstructors={sortedInstructors.length}
              instructorLink={
                disableCTA ? (
                  styledInstructorName
                ) : (
                  <Link
                    component={TrackedLink2}
                    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                    href={getInstructorPageUrl(firstInstructor.id, firstInstructor.shortName)}
                    trackingName="hero_instructor"
                    typographyVariant="bodyPrimary"
                    css={styles.instructorLink}
                    onClick={onClickFirstInstructor}
                  >
                    {styledInstructorName}
                  </Link>
                )
              }
              moreInstructors={
                sortedInstructors.length > 1 &&
                (disableCTA ? (
                  styledInstructorsLabel
                ) : (
                  <Button
                    variant="ghost"
                    size="small"
                    edgeAlign="start"
                    css={styles.moreInstructorsButton}
                    component={TrackedButton}
                    withVisibilityTracking={false}
                    requireFullyVisible={false}
                    trackingName="hero_instructors_modal_link"
                    onClick={onClickViewMoreInstructors}
                  >
                    {styledInstructorsLabel}
                  </Button>
                ))
              }
            />
          </Typography2>
          {sortedInstructors.length > 1 && (
            <InstructorsModal
              open={isModalOpen}
              setOpen={setModalOpen}
              instructors={sortedInstructors}
              averageInstructorRating={averageInstructorRating}
              instructorRatingCount={instructorRatingCount}
              partners={partners}
              width="large"
              closeModalTrackingName="close_instructors_modal_hero_banner"
            />
          )}
        </div>
        <HeroBadges slug={slug} isTopInstructor={Boolean(firstInstructor?.isTopInstructor)} />
      </div>
    </div>
  );
};

export default HeroInstructors;
